import { useEffect, useCallback, useRef } from 'react';
import { Spin } from 'antd';
import { useSearchParams, useNavigate } from 'react-router-dom';

import i18n from '@/shared/i18n';
import { getFederatedLoginRequired } from '@/shared/utils/apiError';
import type { FederatedLoginLocationState } from '@/features/auth/types/auth.types';
import {
  useAcceptInvitation,
  useFirstAccessIdentify,
} from '@/features/auth/hooks';
import { useJoinStore } from '@/features/auth/store/join';
import {
  isAuthenticated as isStorageAuthenticated,
  setOnboardingToken,
} from '@/shared/api/storage';
import AuthMessage from '@/shared/components/ui/molecules/AuthMessage';

import './JoinPage.css';

/**
 * JoinPage
 *
 * Entry point for invitation URL processing.
 * Routes users based on existingUser param:
 *
 * - existingUser=false → redirect to /create-password
 * - existingUser=true + not logged in → redirect to /login
 * - existingUser=true + logged in → call accept-invitation API → redirect to /home
 *
 * Route: /join
 * Query params (regular invite):
 * - inviteId: string
 * - token: string
 * - existingUser: 'true' | 'false'
 *
 * Query params (Business Account owner onboarding):
 * - username: string  — email address from the onboarding email
 * - validationCode: string — OTP from the onboarding email
 *
 * @since 1.0.0
 * @category Pages
 */
const JoinPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Ref to prevent duplicate API calls (React Strict Mode calls useEffect twice)
  const hasCalledApi = useRef(false);

  const { currentStep, invitationAccepted, invitationFailed, reset } =
    useJoinStore();

  const inviteId = searchParams.get('inviteId');
  const token = searchParams.get('token');
  const existingUser = searchParams.get('existingUser');

  // Onboarding params (Business Account owner first access)
  const username = searchParams.get('username');
  const validationCode = searchParams.get('validationCode');

  // Use isAuthenticated() (stored session + expiry check) for consistency
  // with PublicRoute/PrivateRoute guards, avoiding redirect loops when the
  // token has expired but Zustand state lingers.
  const isLoggedIn = isStorageAuthenticated();

  const { mutateAsync: acceptInvitation } = useAcceptInvitation({
    isAuthenticated: isLoggedIn,
    onSuccess: () => {
      invitationAccepted();
      navigate('/', { replace: true });
    },
    onError: (error: Error) => {
      invitationFailed(error.message || i18n.t('Failed to accept invitation'));
    },
  });

  const { mutate: identifyFirstAccess } = useFirstAccessIdentify({
    onSuccess: (data) => {
      setOnboardingToken(data.accessToken);
      invitationAccepted();
      navigate('/create-password', {
        // The onboarding token stays in memory — no token travels in router
        // (history) state.
        state: { firstAccess: true },
        replace: true,
      });
    },
    onError: (error: Error) => {
      // CA07 — primeiro acesso de usuário federado: a definição de senha não
      // é solicitada; o usuário é conduzido ao fluxo corporativo com o
      // e-mail do onboarding preservado.
      const federated = getFederatedLoginRequired(error);
      if (federated) {
        const state: FederatedLoginLocationState = {
          email: username ?? undefined,
          reason: 'federated-required',
          registrationId: federated.registrationId,
          authenticationUrl: federated.authenticationUrl,
        };
        navigate('/sso', { state, replace: true });
        return;
      }
      invitationFailed(
        error.message || i18n.t('Failed to validate onboarding link')
      );
    },
  });

  /**
   * Handle the accept invitation API call
   */
  const handleAcceptInvitation = useCallback(async () => {
    if (!inviteId || !token || !isLoggedIn) {
      invitationFailed(i18n.t('Invalid invitation parameters'));
      return;
    }

    try {
      await acceptInvitation({ inviteId, token });
    } catch {
      // Error already handled in onError callback
    }
  }, [inviteId, token, isLoggedIn, acceptInvitation, invitationFailed]);

  useEffect(() => {
    // Prevent duplicate API calls (React Strict Mode calls useEffect twice)
    if (hasCalledApi.current) {
      return;
    }

    // Reset store on mount
    reset();

    // --- Onboarding path: Business Account owner first access ---
    // Detected by username + validationCode params (no inviteId)
    if (username && validationCode) {
      hasCalledApi.current = true;
      identifyFirstAccess({ username, otp: validationCode });
      return;
    }

    // Validate required params
    if (!inviteId || !token) {
      invitationFailed(
        i18n.t('Invalid invitation link. Missing required parameters.')
      );
      return;
    }

    // Route based on existingUser param
    if (existingUser === 'false') {
      // New user flow: redirect to create password page
      navigate(
        `/create-password?inviteId=${inviteId}&token=${token}&existingUser=false`,
        { replace: true }
      );
      return;
    }

    if (existingUser !== 'true') {
      invitationFailed(i18n.t('Invalid existingUser parameter'));
      return;
    }

    // existingUser=true: check if logged in
    if (!isLoggedIn) {
      // Not logged in: redirect to login with return URL
      const returnUrl = encodeURIComponent(
        `/join?inviteId=${inviteId}&token=${token}&existingUser=true`
      );
      navigate(`/login?returnUrl=${returnUrl}`, { replace: true });
      return;
    }

    // Mark that we're about to call the API
    hasCalledApi.current = true;

    // Logged in: accept the invitation
    handleAcceptInvitation();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Loading state - only check store state
  if (currentStep === 'loading') {
    return (
      <div className="auth-page-container">
        <div className="auth-container">
          <div className="join-loading">
            <Spin size="large" />
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (currentStep === 'error') {
    return (
      <div className="auth-page-container">
        <div className="auth-container">
          <AuthMessage
            subtitle={i18n.t('Identity and Fraud Platform')}
            title={i18n.t('Make a new request')}
            description={i18n.t(
              'Attention! There was an error in your validation process.'
            )}
            alert={{
              message: i18n.t(
                'No access! Ask your requester to resend a new registration link.'
              ),
              type: 'error',
            }}
          />
        </div>
      </div>
    );
  }

  if (currentStep === 'success') {
    return (
      <div className="auth-page-container">
        <div className="auth-container">
          <div className="join-loading">
            <Spin size="large" />
            <p className="join-loading-text">
              {i18n.t('Invitation accepted! Redirecting...')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page-container">
      <div className="auth-container">
        <div className="join-loading">
          <Spin size="large" />
        </div>
      </div>
    </div>
  );
};

export default JoinPage;
